import { useCallback } from "react";
import EmailPreferencesForm from "../../../../pages/EmailPreferences/EmailPreferencesForm";
import { emailPreferencesConfigs } from "../../../../pages/EmailPreferences/constants";
import {
  EmailType,
  IEmailPreference,
  IUnsubscription,
  TEmailPreferenceRecord,
} from "../../../../pages/EmailPreferences/types";
import { IWizardConfig } from "../../../../wizard/types";
import useUserQueries from "../../../hooks/useUserQueries";
import { useUserId } from "../../../hooks/useUserId";
import { UserRole } from "../../../enums/UserRole";

export function useUpdateEmailPreferencesConfig(): IWizardConfig {
  const { fetchEmailPreferences, updateEmailPreferences } = useUserQueries();
  const userId = useUserId();

  const onSave = useCallback(async (data) => {
    const unsubscriptions = Object.entries(data.preferences).reduce(
      (acc, [key, value]) => {
        if (
          (!value && key !== EmailType.All) ||
          (value && key === EmailType.All)
        ) {
          acc.push({ type: key as EmailType });
        }

        return acc;
      },
      [] as IUnsubscription[],
    );

    return await updateEmailPreferences(userId, unsubscriptions);
  }, []);

  const getInitialData = useCallback(async () => {
    const { data } = await fetchEmailPreferences(userId);

    const initialData: { role: UserRole; preferences: TEmailPreferenceRecord } =
      {
        role: data.role,
        preferences: {} as TEmailPreferenceRecord,
      };

    const preferences: TEmailPreferenceRecord = emailPreferencesConfigs[
      data.role
    ].reduce((acc: TEmailPreferenceRecord, config: IEmailPreference) => {
      acc[config.type] = config.defaultValue;

      return acc;
    }, {} as TEmailPreferenceRecord);

    // applying received data
    data.unsubscriptions.forEach((item: IUnsubscription) => {
      // set false if not "All", otherwise set to true
      preferences[item.type] = item.type === EmailType.All;
    });

    initialData.preferences = preferences;

    return initialData;
  }, [userId]);

  return {
    steps: [
      {
        width: "540px",
        titleConfig: {
          titleText: "Update Email Preferences",
        },
        content: EmailPreferencesForm,
      },
    ],
    submitButtonText: "Update",
    saveCallback: onSave,
    getInitialData: getInitialData,
  };
}
